/**
 * The two one-line pipelines under the Dashboard's Pipeline Snapshot
 * ("Collings Leads" and "... Own Pipeline") follow the snapshot's agent
 * picker (DOMA 13343551930): one picked agent reads that agent's analytics
 * row, "All agents" / "Whole team" adds the rows of that group up, and
 * "My pipeline" reads the viewer's own row.
 */

export const FUNNEL_FIELDS = [
  "collings_leads",
  "collings_contacted",
  "collings_appraisals",
  "collings_listings_won",
  "collings_sales",
  "collings_leads_lost",
  "agent_leads",
  "agent_appraisals",
  "agent_listings_won",
  "agent_sales",
  "agent_leads_lost",
];

/** Sum the funnel fields across analytics rows (one per agent). */
export const sumFunnelRows = (rows) =>
  (rows || []).reduce((acc, row) => {
    FUNNEL_FIELDS.forEach((f) => {
      acc[f] = (acc[f] || 0) + (Number(row?.[f]) || 0);
    });
    return acc;
  }, {});

/** Label for the own-pipeline strip, by whose numbers are on screen. */
export const ownPipelineLabel = ({ boardAgentName, team, agencyWide }) => {
  if (boardAgentName) return `${boardAgentName}'s Own Pipeline`;
  if (team) return "Team's Own Pipeline";
  if (agencyWide) return "All Agents' Own Pipeline";
  return "Your Own Pipeline";
};

/**
 * Which analytics request the picker scope needs, or null to reuse the
 * viewer's own "mine" row.
 */
export const funnelRequestFor = ({ boardAgentId, team, agencyWide, pickerMode }) => {
  if (boardAgentId) return { kind: "agent", params: { agent: boardAgentId } };
  if (team || (pickerMode === "admin" && agencyWide)) return { kind: "group", params: {} };
  return null;
};

/**
 * What sits behind each strip number (DOMA 13343551930: "make the numbers
 * clickable to see which leads"): the analytics drill-down metric to fetch,
 * its extra params, and a row filter when one metric's rows serve several
 * stages (lead rows carry `contacted` and an `outcome`; sale rows an `origin`).
 */
const leadDrill = (metric) => ({
  received: { metric, title: "received", filter: null },
  contacted: { metric, title: "contacted", filter: (r) => r.contacted },
  lost: { metric, title: "lost", filter: (r) => r.outcome === "Lost" },
});

export const FUNNEL_DRILLS = {
  collings: {
    ...leadDrill("collings_leads"),
    appraisals: { metric: "appraisals", params: { origin: "collings_generated" }, title: "appraisals" },
    listings: { metric: "listings_won", params: { origin: "collings_generated" }, title: "listings" },
    sold: { metric: "commissions", title: "sold", filter: (r) => r.origin === "collings_generated" },
  },
  own: {
    received: { metric: "agent_leads", title: "opportunities", filter: null },
    lost: { metric: "agent_leads", title: "lost", filter: (r) => r.outcome === "Lost" },
    appraisals: { metric: "appraisals", params: { origin: "agent_generated" }, title: "appraisals" },
    listings: { metric: "listings_won", params: { origin: "agent_generated" }, title: "listings" },
    sold: { metric: "commissions", title: "sold", filter: (r) => r.origin === "agent_generated" },
  },
};

/**
 * Whose rows a drill fetches, as a list of `agent` params for the detail
 * endpoint: one picked agent; every team member for a team's "Whole team"
 * (the endpoint scopes a non-admin to one agent at a time); `null` for an
 * admin's "All agents" (agency-wide); the viewer for "My pipeline".
 */
export const funnelDrillAgents = ({ boardAgentId, team, agencyWide, pickerMode, agentOptions, viewerId }) => {
  if (boardAgentId) return [String(boardAgentId)];
  if (team) return (agentOptions || []).map((a) => String(a.id));
  if (pickerMode === "admin" && agencyWide) return [null];
  return viewerId ? [String(viewerId)] : [];
};
